import React from "react";
import { formatDuration } from "../../../../utils/oversightFormat";
import { formatClockHour } from "./sessionTracks";

/**
 * Card shell for the four presence headlines. Deliberately not `MetricCard`:
 * these carry a plain-English third line explaining what the number means,
 * which `MetricCard`'s `sub` renders too faintly to read as a sentence.
 */
function PresenceCard({ value, label, note, tone = "neutral" }) {
  const valueClass = tone === "warn" ? "text-internal-text" : "text-gray-800";
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className={`text-[22px] leading-7 font-bold ${valueClass}`}>{value}</p>
      <p className="mt-1 text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      {note && <p className="mt-0.5 text-[11px] text-gray-400">{note}</p>}
    </div>
  );
}

/**
 * The four headline numbers above the presence tracks: a usual working day,
 * how many days of the range the coach signed in at all, when they usually
 * start and finish, and how many session ends had to be estimated.
 *
 * @param {Object} props
 * @param {Object} props.summary - Output of `summariseSessionDays`.
 * @param {string} [props.zoneAbbr] - Zone the hours are in, e.g. "GMT+5:30". Omitting it
 *   leaves the card reading exactly as it did before.
 */
export function SessionKpiRow({ summary, zoneAbbr }) {
  const {
    medianOnlineSeconds,
    daysSignedIn,
    totalDays,
    medianStartHour,
    medianEndHour,
    estimatedEnds,
  } = summary;

  const daysOff = Math.max(0, totalDays - daysSignedIn);

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <PresenceCard
        value={formatDuration(medianOnlineSeconds)}
        label="Usual day"
        note="the middle day, not the average"
      />
      <PresenceCard
        value={
          <>
            {daysSignedIn}
            <span className="text-[13px] font-normal text-gray-400"> of {totalDays}</span>
          </>
        }
        label="Days signed in"
        note={daysOff === 0 ? "signed in every day" : `${daysOff} day${daysOff === 1 ? "" : "s"} off`}
      />
      <PresenceCard
        value={
          <>
            {formatClockHour(medianStartHour)}
            <span className="text-[13px] font-normal text-gray-400">
              {" → "}
              {formatClockHour(medianEndHour)}
            </span>
          </>
        }
        label="Usual start and finish"
        note={
          zoneAbbr
            ? `when they typically start and stop · ${zoneAbbr}`
            : "when they typically start and stop"
        }
      />
      <PresenceCard
        value={estimatedEnds}
        label="Ends we had to guess"
        note="the app closed without telling us"
        tone={estimatedEnds > 0 ? "warn" : "neutral"}
      />
    </div>
  );
}

export default SessionKpiRow;
